"use client";

import type { MutableRefObject } from "react";
import { markSpotifyForceReauth } from "@/lib/spotifyAuth";
import { fetchSpotifyAccessToken } from "./spotifyWebPlayer.client";
import {
  buildPlayerAuthErrorMessage,
  describeSpotifyPlayer,
  errorMessage,
  isUsableSpotifyPlayer,
  SPOTIFY_WEB_PLAYER_NAME,
  spotifySharedStore,
  toSpotifyTrackMeta,
  type SpotifyPlayerErrorOptions,
  type SpotifyPlayerLike,
  type SpotifySdkError,
  type SpotifySdkState,
  type SpotifySharedState,
} from "./spotifyWebPlayer.shared";

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady?: () => void;
    Spotify?: {
      Player: new (options: {
        name: string;
        getOAuthToken: (cb: (token: string) => void) => void | Promise<void>;
        volume?: number;
      }) => SpotifyPlayerLike;
    };
  }
}

let sdkLoadingStarted = false;

type SetPlayerError = (
  message: string,
  options?: SpotifyPlayerErrorOptions
) => void;

type PatchSharedState = (patch: Partial<SpotifySharedState>) => void;

type ResetPlayerInstance = (
  reason: string,
  details?: Record<string, unknown>
) => void;

type InitializeSpotifyPlayerParams = {
  patchSharedState: PatchSharedState;
  resetPlayerInstance: ResetPlayerInstance;
  setPlayerError: SetPlayerError;
  syncStateFromShared: () => void;
  playerRef: MutableRefObject<SpotifyPlayerLike | null>;
};

export function initializeSpotifyPlayer({
  patchSharedState,
  resetPlayerInstance,
  setPlayerError,
  syncStateFromShared,
  playerRef,
}: InitializeSpotifyPlayerParams) {
  if (!window.Spotify) {
    return;
  }

  const sharedState = spotifySharedStore.getState();

  if (sharedState.player && !isUsableSpotifyPlayer(sharedState.player)) {
    resetPlayerInstance("discarding invalid shared Spotify player", {
      playerShape: describeSpotifyPlayer(sharedState.player),
    });
  }

  const reusablePlayer = spotifySharedStore.getState().player;
  if (reusablePlayer) {
    playerRef.current = reusablePlayer;
    syncStateFromShared();
    return;
  }

  const player = new window.Spotify.Player({
    name: SPOTIFY_WEB_PLAYER_NAME,
    getOAuthToken: async (cb: (token: string) => void) => {
      try {
        const result = await fetchSpotifyAccessToken();
        if (!result.ok || !result.accessToken) {
          const message = buildPlayerAuthErrorMessage(
            result.payloadError,
            result.status
          );
          const markForReauth =
            result.payloadError === "missing_streaming_scope" ||
            result.status === 401 ||
            result.status === 409;
          if (markForReauth) {
            markSpotifyForceReauth("access_token_rejected", message);
          }
          throw new Error(message);
        }
        cb(result.accessToken);
      } catch (error: unknown) {
        setPlayerError(errorMessage(error, "Failed to get Spotify token"), {
          category: "auth",
          event: "access_token_failed",
          title: "Spotify access token request failed",
          error,
        });
      }
    },
    volume: spotifySharedStore.getState().volume,
  });

  playerRef.current = player;
  patchSharedState({
    player,
  });

  player.addListener("ready", (payload: unknown) => {
    const { device_id } = (payload ?? {}) as { device_id?: string };
    if (!device_id) return;
    patchSharedState({
      deviceId: device_id,
      isReady: true,
      needsTransfer: false,
      error: null,
    });
  });

  player.addListener("not_ready", (payload: unknown) => {
    const { device_id } = (payload ?? {}) as { device_id?: string };
    if (!device_id) return;
    const currentDeviceId = spotifySharedStore.getState().deviceId;
    patchSharedState({
      deviceId:
        !currentDeviceId || currentDeviceId === device_id
          ? null
          : currentDeviceId,
      isReady: false,
      needsTransfer: true,
      hasPlaybackState: false,
      error: null,
    });
  });

  player.addListener("player_state_changed", (stateArg: unknown) => {
    const state = stateArg as SpotifySdkState | null;
    if (!state) return;

    const paused = state.paused;
    const position = state.position;
    const duration = state.duration;
    const current = state.track_window?.current_track;
    const nextTrack = toSpotifyTrackMeta(current);
    const currentSharedTrack = spotifySharedStore.getState().currentTrack;

    patchSharedState({
      isPlaying: !paused,
      positionMs: position,
      durationMs: duration,
      isReady: true,
      needsTransfer: false,
      hasPlaybackState: true,
      error: null,
      currentTrack:
        nextTrack && currentSharedTrack?.id !== nextTrack.id
          ? nextTrack
          : currentSharedTrack,
    });
  });

  player.addListener("initialization_error", (event: unknown) => {
    const { message } = (event ?? {}) as SpotifySdkError;
    const msg = message || "Initialization error";
    setPlayerError(msg, {
      event: "player_initialization_error",
      details: {
        sdkMessage: message ?? null,
      },
    });
  });

  player.addListener("authentication_error", (event: unknown) => {
    const { message } = (event ?? {}) as SpotifySdkError;
    const detail =
      typeof message === "string" && message.trim() ? message.trim() : null;
    const msg = detail
      ? `${detail}. Reauthorize Spotify and try again.`
      : "Spotify playback authorization failed. Reauthorize Spotify and try again.";
    setPlayerError(msg, {
      category: "auth",
      markForReauth: true,
      reason: "player_authentication_error",
      event: "player_authentication_error",
      title: "Spotify playback authorization failed",
      details: {
        sdkMessage: detail,
      },
    });
  });

  player.addListener("account_error", (event: unknown) => {
    const { message } = (event ?? {}) as SpotifySdkError;
    const detail =
      typeof message === "string" && message.trim() ? message.trim() : null;
    const msg = detail
      ? `${detail}. Use a Premium Spotify account or reauthorize with the correct account.`
      : "Spotify playback is unavailable for this account. Use a Premium Spotify account or reauthorize with the correct account.";
    setPlayerError(msg, {
      category: "auth",
      markForReauth: true,
      reason: "player_account_error",
      event: "player_account_error",
      title: "Spotify account cannot use playback",
      details: {
        sdkMessage: detail,
      },
    });
  });

  player.addListener("playback_error", (event: unknown) => {
    const { message } = (event ?? {}) as SpotifySdkError;
    const msg = message || "Playback error";
    setPlayerError(msg, {
      event: "player_playback_error",
      details: {
        sdkMessage: message ?? null,
      },
    });
  });

  player.addListener("autoplay_failed", () => {
    patchSharedState({
      isPlaying: false,
    });
    setPlayerError(
      "Spotify blocked autoplay on this device. Tap the Spotify button to reactivate playback here.",
      {
        level: "warn",
        event: "autoplay_failed",
        title: "Spotify autoplay was blocked",
      }
    );
  });

  player
    .connect()
    .then((success: boolean) => {
      if (!success) {
        setPlayerError("Failed to connect Web Playback SDK", {
          event: "connect_failed",
        });
      }
    })
    .catch((error: unknown) => {
      setPlayerError(errorMessage(error, "Failed to connect Web Playback SDK"), {
        event: "connect_failed",
        error,
      });
    });
}

export function loadSpotifySdk(initPlayer: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (window.Spotify) {
    initPlayer();
    return () => {};
  }

  const existingScript = document.getElementById(
    "spotify-player"
  ) as HTMLScriptElement | null;

  if (existingScript) {
    window.onSpotifyWebPlaybackSDKReady = () => {
      initPlayer();
    };
    return () => {
      window.onSpotifyWebPlaybackSDKReady = () => {};
    };
  }

  if (!sdkLoadingStarted) {
    sdkLoadingStarted = true;
    const script = document.createElement("script");
    script.id = "spotify-player";
    script.src = "https://sdk.scdn.co/spotify-player.js";
    script.async = true;
    document.body.appendChild(script);

    window.onSpotifyWebPlaybackSDKReady = () => {
      initPlayer();
    };
  }

  return () => {
    window.onSpotifyWebPlaybackSDKReady = () => {};
  };
}
